/**
 * AWS Cognito Configuration - Backend-Served Runtime Config
 *
 * Zero-config frontend: All configuration is fetched from the backend API.
 *
 * Local Development:
 *   - Start medi-flow backend (http://localhost:8890)
 *   - Start view-output (npm start)
 *   - Config auto-fetches from backend
 *   - No manual setup required
 *
 * Production:
 *   - Fetches config from backend API automatically
 *   - Single source of truth (AWS Secrets Manager via backend)
 *
 * Why Frontend Needs Cognito Config:
 *   - Frontend must build OAuth authorization URL to redirect user to Cognito Hosted UI
 *   - This is standard OAuth 2.0 flow for browser-based apps ("public clients")
 *   - Client ID is public by design (not a secret)
 */

import { getApiBaseUrl } from "./api";

export interface CognitoConfig {
  domain: string;
  clientId: string;
  redirectUri: string;
  scopes: string[];
  region: string;
}

// Cache for runtime configuration
let cachedCognitoConfig: CognitoConfig | null = null;
let configLoadPromise: Promise<void> | null = null;

/**
 * Load Cognito configuration from backend API
 *
 * This should be called once during application initialization (before authentication flows)
 */
export async function loadCognitoConfig(): Promise<void> {
  // If already loaded or loading, reuse the existing promise
  if (cachedCognitoConfig) {
    return Promise.resolve();
  }

  if (configLoadPromise) {
    return configLoadPromise;
  }

  configLoadPromise = (async () => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/client-config`, {
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error(`Failed to load client config from backend: ${response.status}`);
      }

      const clientConfig = await response.json();

      if (!clientConfig.cognito || !clientConfig.cognito.clientId || !clientConfig.cognito.domain) {
        throw new Error(
          "Invalid client config response from backend: missing Cognito configuration",
        );
      }

      if (!clientConfig.cognito.region) {
        throw new Error("Invalid client config response from backend: missing Cognito region");
      }

      cachedCognitoConfig = {
        clientId: clientConfig.cognito.clientId,
        domain: clientConfig.cognito.domain,
        region: clientConfig.cognito.region,
        redirectUri: getCognitoRedirectUri(),
        scopes: ["openid", "email", "profile"],
      };
    } catch (err) {
      // Reset so next call retries the fetch instead of returning cached rejection
      configLoadPromise = null;
      // Local development can still start the OAuth flow when the proxy is
      // temporarily unavailable. These are public Cognito client settings,
      // supplied through .env.local; never use this fallback in production.
      const localClientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
      const localDomain = import.meta.env.VITE_COGNITO_DOMAIN;
      const localRegion = import.meta.env.VITE_COGNITO_REGION;
      if (import.meta.env.DEV && localClientId && localDomain && localRegion) {
        cachedCognitoConfig = {
          clientId: localClientId,
          domain: localDomain,
          region: localRegion,
          redirectUri: getCognitoRedirectUri(),
          scopes: ["openid", "email", "profile"],
        };
        return;
      }
      const detail = err instanceof Error ? err.message : String(err);
      throw new Error(`Failed to load Cognito config: ${detail}`);
    }
  })();

  return configLoadPromise;
}

/**
 * Get the Cognito redirect URI
 * This MUST match the callback_urls configured in Cognito User Pool Client
 */
export const getCognitoRedirectUri = (): string => {
  return `${window.location.origin}/oauth/callback`;
};

/**
 * Get Cognito client ID
 * Throws if config hasn't been loaded yet
 */
export const getCognitoClientId = (): string => {
  if (!cachedCognitoConfig) {
    throw new Error(
      "Cognito configuration not loaded. Call loadCognitoConfig() during app initialization.",
    );
  }
  return cachedCognitoConfig.clientId;
};

/**
 * Get Cognito domain
 * Throws if config hasn't been loaded yet
 */
export const getCognitoDomain = (): string => {
  if (!cachedCognitoConfig) {
    throw new Error(
      "Cognito configuration not loaded. Call loadCognitoConfig() during app initialization.",
    );
  }
  return cachedCognitoConfig.domain;
};

/**
 * Get Cognito region
 * Throws if config hasn't been loaded yet
 */
export const getCognitoRegion = (): string => {
  if (!cachedCognitoConfig) {
    throw new Error(
      "Cognito configuration not loaded. Call loadCognitoConfig() during app initialization.",
    );
  }
  return cachedCognitoConfig.region;
};

/**
 * Get complete Cognito configuration
 * Throws if config hasn't been loaded yet
 */
export const getCognitoConfig = (): CognitoConfig => {
  if (!cachedCognitoConfig) {
    throw new Error(
      "Cognito configuration not loaded. Call loadCognitoConfig() during app initialization.",
    );
  }
  return cachedCognitoConfig;
};

/**
 * Build Cognito Hosted UI authorization URL
 *
 * This URL redirects the user to Cognito's hosted login page,
 * which then redirects to Google OAuth for authentication.
 */
export const buildCognitoAuthUrl = (): string => {
  const config = getCognitoConfig();

  const params = new URLSearchParams({
    response_type: "code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scopes.join(" "),
    // Identity provider hint (skip Cognito login page, go straight to Google)
    identity_provider: "Google",
  });

  return `https://${config.domain}/oauth2/authorize?${params.toString()}`;
};

/**
 * Build Cognito logout URL
 */
export const buildCognitoLogoutUrl = (): string => {
  const config = getCognitoConfig();

  const params = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: window.location.origin,
  });

  return `https://${config.domain}/logout?${params.toString()}`;
};

/**
 * Check if running in local development mode (no real Cognito)
 */
export const isLocalDevMode = (): boolean => {
  if (!cachedCognitoConfig) {
    return false;
  }
  return cachedCognitoConfig.domain === "local-dev";
};
